<template>
    <TprmModal
        additional-class-name="RiskProfileSetupModal"
        open
        :title="modalTitle"
        @close="abort"
    >
        <div v-if="collateralEnabled" data-test-id="RiskProfileSetupModal_typeSelection">
            <div
                class="flex justify-around"
                data-test-id="RiskProfileSetupModal__requirementTypesRiskProfileContainer"
            >
                <RiskProfileTypeSelectionCard
                    :selected="riskProfileType === defaultRiskProfileTypeConfig.type"
                    :type="defaultRiskProfileTypeConfig.type"
                    :title="defaultRiskProfileTypeConfig.title"
                    :description="defaultRiskProfileTypeConfig.description"
                    @input="selectRiskProfileType"
                />
                <RiskProfileTypeSelectionCard
                    :selected="riskProfileType === collateralsTypeConfig.type"
                    :type="collateralsTypeConfig.type"
                    :title="collateralsTypeConfig.title"
                    :description="collateralsTypeConfig.description"
                    @input="selectRiskProfileType"
                />
            </div>
        </div>
        <FormElement
            class="RiskProfileSetupModal__riskProfileName"
            label="Name Risk Profile:"
        >
            <template #labelHint>
                This can be edited later.
                <span class="RiskProfileSetupModal__requiredHint">(Required)</span>
            </template>
            <FormInput>
                <input v-model="riskProfileName" type="text">
            </FormInput>
        </FormElement>
        <div class="RiskProfileSetupModal__contentText">
            Select Country:
        </div>
        <NewDropdown
            class="RiskProfileSetupModal__countryDropdown"
            :options="options"
            :selected="selectedCountryCode"
            @input="selectOption"
        />
        <template #footer>
            <NewButton color="danger" variant="outline" @click="abort">
                Cancel
            </NewButton>
            <NewButton color="primary" :disabled="!canConfirm" @click="confirm">
                Create Risk Profile
            </NewButton>
        </template>
    </TprmModal>
</template>

<script lang="ts">
    import { PropType } from 'vue';

    import { Component, Prop, Vue } from '@evidentid/vue-property-decorator';

    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

    import NewDropdown from '@evidentid/dashboard-commons/components/Dropdown/NewDropdown.vue';
    import { DropdownOption } from '@evidentid/dashboard-commons/components/Dropdown/types';
    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { FormElement, FormInput } from '@evidentid/dashboard-commons/components/Form';
    import { RiskProfileType } from '@evidentid/tprm-portal-lib/models/decisioning';

    import NewButton from '@/components/new-button/NewButton.vue';
    import TprmModal from '@/components/tprm-modal/TprmModal.vue';
    import { CategorizedEnumLabels } from '@/modules/dashboard/models/CategorizedEnumLabels.model';
    import { EnumCategories } from '@/modules/dashboard/models/EnumCategories.model';
    import RiskProfileTypeSelectionCard
        from '@/modules/decisioning-criteria/components/RiskProfileTypeSelectionCard/RiskProfileTypeSelectionCard.vue';
    import { ConfirmEventPayload } from '@/modules/decisioning-criteria/models/ConfirmEventPayload.model';
    import { getEnumDefaultDisplayValue } from '@/utils/getEnumDefaultDisplayValue';

    @Component({
        emits: [ 'abort', 'confirm' ],
        components: {
            NewButton,
            TprmModal,
            RiskProfileTypeSelectionCard,
            NewDropdown,
            EidIcon,
            FontAwesomeIcon,
            FormElement,
            FormInput,
        },
    })
    export default class RiskProfileSetupModal extends Vue {
        @Prop({ type: Array as PropType<string[]>, default: (): string[] => [] })
        private countryCodeList!: string[];

        @Prop({ type: Object as PropType<CategorizedEnumLabels>, default: (): CategorizedEnumLabels => ({}) })
        private categorizedEnumLabels!: CategorizedEnumLabels;

        @Prop({ type: Boolean as PropType<boolean>, default: false })
        private collateralEnabled!: boolean;

        private selectedCountryCode: DropdownOption | null = null;
        private riskProfileName: string = '';
        private riskProfileType: RiskProfileType = RiskProfileType.default;
        private defaultRiskProfileTypeConfig = {
            title: 'Requirement-Based Risk Profile',
            type: RiskProfileType.default,
            description: 'Set assignment rules for specific entity and conditions for requirements.',
        };
        private collateralsTypeConfig = {
            title: 'Collateral-Based Risk Profile',
            type: RiskProfileType.collateral,
            description: 'Choose requirements, set assignment rules, map collateral, and set decision criteria.',
        };

        private get modalTitle(): string {
            return this.collateralEnabled
                ? 'Select the type of risk profile you are creating:'
                : 'Create a New Risk Profile';
        }

        private get selectedOption(): DropdownOption | null {
            return this.selectedCountryCode;
        }

        private get options(): DropdownOption[] {
            return this.countryCodeList.map((code) => ({
                label:
                    getEnumDefaultDisplayValue(EnumCategories.countryCode, code, this.categorizedEnumLabels) as string,
                value: code.toUpperCase(),
            }));
        }

        private get defaultOption(): DropdownOption | null {
            const option = this.options.find((option) => option.value === 'US') || this.options[0] || null;
            return option ?
                {
                    ...option,
                    label: getEnumDefaultDisplayValue(
                        EnumCategories.countryCode, option.value, this.categorizedEnumLabels,
                    ),
                }
                : null;
        }

        private mounted() {
            this.selectedCountryCode = this.defaultOption;
        }

        private selectOption(option?: DropdownOption): void {
            if (!option) {
                return;
            }
            this.selectedCountryCode = option;
        }

        private get canConfirm(): boolean {
            return Boolean(this.riskProfileName);
        }

        private confirm(): void {
            this.$emit('confirm', {
                countryCode: this.selectedCountryCode?.value,
                displayName: this.riskProfileName,
                riskProfileType: this.riskProfileType,
            } as ConfirmEventPayload);
        }

        private selectRiskProfileType(type: RiskProfileType): void {
            this.riskProfileType = type;
        }

        private abort(): void {
            this.$emit('abort');
        }
    }
</script>
